import React, { useState, useEffect, createContext, useContext } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { useNotificationLabels } from "./useNotificationsLabels";
import { defaultLabels } from "./constants";
import { ALERTS_REDIRECT_MAP } from "../../const/const";

const NotificationsContext = createContext({
	notifications: [],
	hasMadeApiCall: false,
	refreshNotifications: () => {},
});

const useNotifications = () => useContext(NotificationsContext);

const NotificationsProvider = ({ children, locale = "en-CA" }) => {
	// Manage localizations
	const providedLabels = useNotificationLabels();
	const labels = { ...defaultLabels, ...providedLabels };
	const [notifications, setNotifications] = useState([]);
	const language = locale?.substring(0, 2) ?? "en";
	const currentYear = dayjs().year();

	const loadLiveData = async (controller = new AbortController()) => {
		await fetch(`/api/notifications?locale=${locale}`, { signal: controller.signal })
			.then(res => res.json())
			.then(liveData => {
				const liveNotifications = liveData.notifications.items
					.map(notification => {
						let entryUrl,
							activeYear,
							title,
							date = notification.sys.publishedAt,
							type = "",
							heading = "",
							message = "";

						const entry = notification.entry;
						const entryTypeName = entry?.__typename;

						// Generate URLs
						switch (entryTypeName) {
							case "HotelChain":
								type = "offer";
								title = notification.node?.heading ?? labels.menuHeadingOffer;
								heading = notification.node?.subheading ?? heading;
								entryUrl = `/${language}/hotels/hotel-brands/${entry.slug}`;
								break;

							case "Destination":
								type = "offer";
								title = notification.node?.heading ?? labels.menuHeadingOffer;
								heading = notification.node?.subheading ?? heading;
								entryUrl = `/${language}/destinations/${entry.slug}`;
								break;

							case "Page":
								type = "offer";
								title = notification?.heading ?? labels.menuHeadingOffer;
								heading = notification?.subheading ?? heading;
								entryUrl = `/${language}${entry.slug.startsWith("/") ? entry.slug : `/${entry.slug}`}`;
								break;

							case "TravelAdvisory":
								// Don't adjust for localtime
								date = dayjs(entry.activeDate).utc(false).format("YYYY-MM-DD");
								activeYear = dayjs(entry.activeDate).utc(false).year();
								type = "alert";
								title = notification?.heading ?? labels.menuHeadingAlert;
								message = entry?.content?.json?.content?.[0]?.content?.[0]?.value;
								entryUrl = `/${ALERTS_REDIRECT_MAP[`${language}-CA`]}${activeYear < currentYear ? `/${activeYear}` : ""}#${entry.sys.id}`;
								break;

							default:
								break;
						}

						return {
							type,
							title: notification.heading ?? title,
							heading: notification.subheading ?? heading,
							message: notification?.message?.json?.content?.[0]?.content?.[0]?.value
								? notification.message.json.content[0].content[0].value
								: message,
							date: date,
							url: notification?.url ? notification.url : entryUrl,
							urlTarget: "_self",
							id: notification.sys.id,
						};
					})
					.sort((a, b) => new Date(b.date) - new Date(a.date));

				setNotifications(liveNotifications);
			})
			.catch(error => {
				console.error(`Notification: ${error}`);
			});
	};

	const visibilityCallback = () => {
		if (document.visibilityState === "visible") {
			(async () => {
				await loadLiveData();
			})();
		}
	};

	const refreshNotifications = async controller => {
		try {
			await loadLiveData(controller);
		} catch (error) {
			console.error("refreshNotifications error: ", error);
		}
	};

	useEffect(() => {
		const controller = new AbortController();
		refreshNotifications(controller);

		return () => {
			controller.abort();
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [locale]);

	useEffect(() => {
		// Check the session on window focus
		window.addEventListener("visibilitychange", visibilityCallback);

		return () => {
			window.removeEventListener("visibilitychange", visibilityCallback);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const value = {
		notifications,
		refreshNotifications,
	};

	return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

NotificationsProvider.propTypes = {
	children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
	locale: PropTypes.string,
};

export { NotificationsProvider, useNotifications };
